import { useWindowResize } from '@toggle/helpers';
import { TransformedXAxisType } from '@toggle/toggle';
import { getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useRef } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  SingleSeries,
} from '../apache-config';
import { tooltipFormatter } from '../utils';
import { formatAxis } from './utils';

export interface BasicBarChartProps extends ApacheChartBase {
  chartType: 'apache-bar';
  axis: TransformedXAxisType;
  series: SingleSeries[];
  isHorizontal?: boolean;
}

export const BasicBarChart = ({
  theme = 'dark_default',
  chartType,
  axis,
  series,
  isHorizontal = false,
  onInit,
  isModal,
}: BasicBarChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    const chartDom = init(ref.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const seriesData: echarts.SeriesOption[] = series.map(i => ({
      name: i.name,
      data: i.data,
      type: 'bar',
    }));

    const option = createApacheConfig({
      theme,
      isModal,
      options: {
        ...formatAxis(axis, isHorizontal),
        tooltip: {
          formatter: params => {
            return tooltipFormatter({
              isShowTitle: false,
              isShowMarker: true,
              params: params as Types.DefaultLabelFormatterCallbackParams,
            });
          },
        },
        series: seriesData,
      },
    });

    chartDom.setOption(option);

    if (ref.current) {
      onInit?.(ref.current);
    }

    return () => {
      chartDom.dispose();
    };
  }, [chartType, theme, axis, series, width, height, isModal]);

  return <div ref={ref} />;
};
